import { useState, useContext } from "react";
import { Box, Button, CircularProgress, Paper, TableContainer, Typography } from "@mui/material";
import { Close, LockOpen, Send } from "@mui/icons-material";
import { useOktaAuth } from "@okta/okta-react";
import { ModuleContext } from "../../context/ModuleContext";
import { UserModuleContext } from "../../context/UserModuleContext";

const WSU_RED = "#A60F2D";

export const UnlockAccount = () => {
  const [state, setState] = useState({ waiting: false, error: null });

  const { setModule } = useContext(ModuleContext);
  const { userModule, setUserModule } = useContext(UserModuleContext);
  const { authState } = useOktaAuth();

  const unlockAccount = async () => {
    setState((prev) => ({ ...prev, waiting: true }));

    try {
      const res = await fetch(
        `${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/account/unlock`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: authState.accessToken.accessToken,
          },
          body: JSON.stringify({
            oktaId: userModule.userData["Okta ID"],
            comment: "Account unlocked",
            callerId: authState.accessToken.claims.sub,
          }),
        }
      );

      await res.json();

      setUserModule((prev) => ({
        ...prev,
        userData: { ...prev.userData, "Okta Status": "PASSWORD_EXPIRED" },
      }));
      setState((prev) => ({ ...prev, waiting: false, error: null }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        waiting: false,
        error: { message: "Attempt to unlock account failed." },
      }));
    }
  };

  const oktaStatus = userModule.userData["Okta Status"];
  const networkId = userModule.userData["Network ID"];

  if (state.waiting) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress style={{ color: WSU_RED }} size={80} thickness={5} />
      </Box>
    );
  }

  if (state.error) {
    return (
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ backgroundColor: WSU_RED, color: "white", px: 3, py: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            Action failed
          </Typography>
          <Typography variant="body2">{state.error.message}</Typography>
        </Box>
      </TableContainer>
    );
  }

  if (oktaStatus === "ACTIVE") {
    return (
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
            Unlock Account
          </Typography>
          <Typography variant="caption" sx={{ display: "block", color: "#777", mt: 0.25 }}>{networkId}</Typography>
        </Box>
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Account {networkId} is not locked. No further action to be taken.
          </Typography>
        </Box>
      </TableContainer>
    );
  }

  if (oktaStatus === "LOCKED_OUT") {
    return (
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
            Unlock Account
          </Typography>
          <Typography variant="caption" sx={{ display: "block", color: "#777", mt: 0.25 }}>{networkId}</Typography>
        </Box>
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Unlock account {networkId}?
          </Typography>
        </Box>
        <Box sx={{ px: 3, pb: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button
            variant="outlined"
            endIcon={<Close />}
            onClick={() => setModule((prev) => ({ ...prev, isVisible: false }))}
            sx={{ borderColor: "#ccc", color: "#666", "&:hover": { borderColor: "#aaa", backgroundColor: "#f9f9f9" } }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            endIcon={<LockOpen />}
            onClick={unlockAccount}
            sx={{
              backgroundColor: WSU_RED,
              color: "white",
              px: 4,
              py: 1.25,
              fontWeight: 600,
              letterSpacing: 0.5,
              "&:hover": { backgroundColor: "#8a0c25" },
            }}
          >
            Unlock Account
          </Button>
        </Box>
      </TableContainer>
    );
  }

  return (
    <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
      <Box sx={{ px: 3, pt: 3, pb: 1 }}>
        <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
          Unlock Account
        </Typography>
        <Typography variant="caption" sx={{ display: "block", color: "#777", mt: 0.25 }}>{networkId}</Typography>
      </Box>
      <Box sx={{ px: 3, py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          User {networkId} is in account status {oktaStatus} and is not eligible for this action.
        </Typography>
      </Box>
    </TableContainer>
  );
};
